// Purpose: user card at the bottom of the Sidebar. Shows avatar
// (initials from displayName), display name, role badge, and a kebab
// button that opens a popover with:
//   * Theme segmented control (auto/light/dark)
//   * Sign out
//
// Popover closes on outside click + Escape. Role badge uses StatusPill
// tones (neutral for USER, info for ADMIN, muted for READONLY).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.tsx';
import { LogOutIcon, MoreVerticalIcon } from '../icons/index.tsx';
import { ThemeToggle } from '../ui/ThemeToggle.tsx';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

const ROLE_LABEL: Record<string, string> = {
  USER: 'User',
  ADMIN: 'Admin',
  READONLY: 'Read-only',
};

const ROLE_CLASS: Record<string, string> = {
  USER: 'text-fg-muted',
  ADMIN: 'text-accent-primary',
  READONLY: 'text-fg-muted',
};

export function UserMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDown = (ev: MouseEvent) => {
      if (!rootRef.current?.contains(ev.target as Node)) close();
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  if (!user) return null;

  return (
    <div ref={rootRef} className="relative">
      <div className="flex items-center gap-3 p-2 rounded-md">
        <span
          aria-hidden
          className={[
            'flex items-center justify-center flex-shrink-0',
            'h-9 w-9 rounded-full text-white text-body-s font-semibold',
            'bg-[linear-gradient(135deg,var(--accent-gradient-start)_0%,var(--accent-gradient-end)_100%)]',
          ].join(' ')}
        >
          {initials(user.displayName)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-body-s font-semibold text-fg-primary truncate">
            {user.displayName}
          </div>
          <div
            className={['text-caption uppercase', ROLE_CLASS[user.role] ?? 'text-fg-muted'].join(
              ' '
            )}
          >
            {ROLE_LABEL[user.role] ?? user.role}
          </div>
        </div>
        <button
          type="button"
          aria-label="Open account menu"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((p) => !p)}
          className={[
            'h-8 w-8 inline-flex items-center justify-center flex-shrink-0',
            'rounded-md text-fg-secondary cursor-pointer',
            'transition-colors duration-fast ease-out',
            'hover:bg-bg-tertiary hover:text-fg-primary',
          ].join(' ')}
        >
          <MoreVerticalIcon size={16} />
        </button>
      </div>

      {open ? (
        <div
          role="menu"
          className={[
            'absolute bottom-full left-0 right-0 mb-2',
            'rounded-lg border border-border-default bg-surface-elevated shadow-lg',
            'p-3 flex flex-col gap-3',
          ].join(' ')}
        >
          <div className="flex flex-col gap-2">
            <span className="text-label uppercase text-fg-muted">Theme</span>
            <ThemeToggle mode="segmented" />
          </div>
          <div className="border-t border-border-default -mx-3" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              void signOut();
            }}
            className={[
              'flex items-center gap-3 h-10 px-2 rounded-md',
              'text-body-m text-fg-primary cursor-pointer',
              'transition-colors duration-fast ease-out',
              'hover:bg-bg-tertiary',
            ].join(' ')}
          >
            <LogOutIcon size={16} className="text-fg-muted" />
            <span>Sign out</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
